import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Droplets, Package, Container, Truck, MoreHorizontal, FileText, Upload } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { AuthedImage, viewFile } from '@/components/common/AuthedImage';
import type { VehicleBody, BodyInput, BodyType, BodyStatus } from '@/lib/types';
import { useLegalEntities, useAllInvestors } from '@/hooks/useVehicles';

interface BodyFormProps {
  initial?: VehicleBody | null;
  onSubmit: (input: BodyInput) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
}

const BODY_TYPES: { value: BodyType; icon: typeof Droplets }[] = [
  { value: 'water_tanker', icon: Droplets },
  { value: 'sewage_tanker', icon: Droplets },
  { value: 'flatbed', icon: Package },
  { value: 'lowbed', icon: Package },
  { value: 'container_carrier', icon: Container },
  { value: 'tipper', icon: Truck },
  { value: 'box_van', icon: Container },
  { value: 'other', icon: MoreHorizontal },
];

const STATUSES: BodyStatus[] = ['active', 'inactive', 'in_repair', 'sold', 'rental_returned'];

// PR-RIYADH-FIX-2 MAY 12 — common axle layouts. axleCount stays a number so
// reports/group-by works; axleConfig is the human label (4x2, 6x4, etc).
const AXLE_CONFIGS = [
  { count: 2, config: '2-axle' },
  { count: 3, config: '3-axle' },
  { count: 4, config: '4-axle / 4x2' },
  { count: 4, config: '4-axle / 6x4' },
  { count: 5, config: '5-axle' },
  { count: 6, config: '6-axle' },
];

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

const blank: BodyInput = {
  bodyType: 'water_tanker',
  serialNumber: '',
  capacityM3: null,
  payloadCapacityKg: null,
  manufacturingYear: null,
  manufacturingCountry: '',
  ownerInvestorId: '',
  legalEntityId: '',
  monthlyRentSar: 0,
  rentStartDate: '',
  rentEndDate: '',
  purchasePriceSar: null,
  purchaseDate: '',
  swaLicenseNumber: '',
  swaLicenseExpiry: '',
  axleCount: null,
  axleConfig: '',
  status: 'active',
  notes: '',
};

export function BodyForm({ initial, onSubmit, onCancel, submitLabel }: BodyFormProps) {
  const { t } = useTranslation();
  const { data: legalEntities = [] } = useLegalEntities();
  const { data: investors = [] } = useAllInvestors();

  const [form, setForm] = useState<BodyInput>(blank);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SWA license file state — same pattern as the Istemarah upload card.
  const [swaFileUrl, setSwaFileUrl] = useState<string | null>(null);
  const [swaUploading, setSwaUploading] = useState(false);
  const [swaError, setSwaError] = useState<string | null>(null);

  useEffect(() => {
    if (initial) {
      setForm({
        bodyType: initial.bodyType,
        serialNumber: initial.serialNumber ?? '',
        capacityM3: initial.capacityM3 != null ? Number(initial.capacityM3) : null,
        payloadCapacityKg: initial.payloadCapacityKg,
        manufacturingYear: initial.manufacturingYear,
        manufacturingCountry: initial.manufacturingCountry ?? '',
        ownerInvestorId: initial.ownerInvestorId,
        legalEntityId: initial.legalEntityId,
        monthlyRentSar: initial.monthlyRentSar != null ? Number(initial.monthlyRentSar) : 0,
        rentStartDate: initial.rentStartDate ? initial.rentStartDate.slice(0, 10) : '',
        rentEndDate: initial.rentEndDate ? initial.rentEndDate.slice(0, 10) : '',
        purchasePriceSar: initial.purchasePriceSar != null ? Number(initial.purchasePriceSar) : null,
        purchaseDate: initial.purchaseDate ? initial.purchaseDate.slice(0, 10) : '',
        swaLicenseNumber: initial.swaLicenseNumber ?? '',
        swaLicenseExpiry: initial.swaLicenseExpiry ? initial.swaLicenseExpiry.slice(0, 10) : '',
        axleCount: initial.axleCount ?? null,
        axleConfig: initial.axleConfig ?? '',
        status: initial.status,
        notes: initial.notes ?? '',
      });
      setSwaFileUrl(initial.swaLicenseFileUrl ? `/api/v1/bodies/${initial.id}/swa-license/file` : null);
    } else {
      const defaultLE = legalEntities.find((le) => le.isOperational) ?? legalEntities[0];
      setForm({ ...blank, legalEntityId: defaultLE?.id ?? '' });
    }
  }, [initial, legalEntities]);

  const update = <K extends keyof BodyInput>(key: K, value: BodyInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const isTanker = form.bodyType === 'water_tanker' || form.bodyType === 'sewage_tanker';

  async function handleSwaFile(f: File) {
    if (!initial?.id) {
      setSwaError('Save the body first, then upload the SWA license.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'application/pdf'].includes(f.type)) {
      setSwaError('File must be JPEG, PNG, or PDF.');
      return;
    }
    if (f.size > 10 * 1024 * 1024) { setSwaError('File too large (10 MB max).'); return; }
    setSwaUploading(true); setSwaError(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const jwt = getStoredToken();
      const res = await fetch(`${API_BASE}/api/v1/bodies/${initial.id}/swa-license`, {
        method: 'POST',
        headers: jwt ? { Authorization: 'Bearer ' + jwt } : {},
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || ('HTTP ' + res.status));
      }
      const json = await res.json() as { url: string };
      // Force AuthedImage to re-fetch with a fresh cache-busting suffix.
      setSwaFileUrl(json.url + '?t=' + Date.now());
    } catch (e) { setSwaError(extractErrorMessage(e)); }
    finally { setSwaUploading(false); }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.ownerInvestorId) { setError(t('bodies.errors.ownerRequired')); return; }
    if (!form.legalEntityId) { setError(t('bodies.errors.legalEntityRequired')); return; }
    const rent = form.monthlyRentSar ?? 0;
    if (rent < 0) { setError('Monthly rent cannot be negative'); return; }

    const payload: BodyInput = {
      bodyType: form.bodyType,
      ownerInvestorId: form.ownerInvestorId,
      legalEntityId: form.legalEntityId,
      monthlyRentSar: rent,
      status: form.status,
    };
    if (form.serialNumber?.trim()) payload.serialNumber = form.serialNumber.trim();
    if (isTanker && form.capacityM3 != null) payload.capacityM3 = form.capacityM3;
    if (!isTanker && form.payloadCapacityKg != null) payload.payloadCapacityKg = form.payloadCapacityKg;
    if (form.manufacturingYear != null) payload.manufacturingYear = form.manufacturingYear;
    if (form.manufacturingCountry?.trim()) payload.manufacturingCountry = form.manufacturingCountry.trim();
    if (rent > 0) {
      if (form.rentStartDate?.trim()) payload.rentStartDate = form.rentStartDate.trim();
      if (form.rentEndDate?.trim()) payload.rentEndDate = form.rentEndDate.trim();
    }
    if (form.purchasePriceSar != null) payload.purchasePriceSar = form.purchasePriceSar;
    if (form.purchaseDate?.trim()) payload.purchaseDate = form.purchaseDate.trim();
    if (form.swaLicenseNumber?.trim()) payload.swaLicenseNumber = form.swaLicenseNumber.trim();
    if (form.swaLicenseExpiry?.trim()) payload.swaLicenseExpiry = form.swaLicenseExpiry.trim();
    if (form.axleCount != null) payload.axleCount = form.axleCount;
    if (form.axleConfig?.trim()) payload.axleConfig = form.axleConfig.trim();
    if (form.notes?.trim()) payload.notes = form.notes.trim();

    setSubmitting(true);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Mute the `apiClient` unused warning when SWA upload isn't wired in this
  // build (kept for parity with other forms that paste-upload via apiClient).
  void apiClient;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Body type */}
        <fieldset className="space-y-3">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t('bodies.section.type')}
          </legend>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {BODY_TYPES.map((bt) => {
              const Icon = bt.icon;
              const selected = form.bodyType === bt.value;
              return (
                <button
                  key={bt.value}
                  type="button"
                  onClick={() => update('bodyType', bt.value)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-md border text-xs font-medium transition-all ${
                    selected
                      ? 'border-gold bg-gold/10 text-foreground'
                      : 'border-border hover:border-muted-foreground/40 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${selected ? 'text-gold' : ''}`} />
                  <span>{t(`bodies.type.${bt.value}`)}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Identity */}
        <fieldset className="space-y-3">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t('bodies.section.identity')}
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={t('bodies.field.serialNumber')}>
              <Input dir="ltr" value={form.serialNumber ?? ''} onChange={(e) => update('serialNumber', e.target.value)} className="font-mono" placeholder="TNK-A-001" />
            </Field>
            {isTanker ? (
              <Field label={t('bodies.field.capacityM3')}>
                <Input type="number" step="0.5" min={0} max={100} dir="ltr"
                  value={form.capacityM3 ?? ''}
                  onChange={(e) => update('capacityM3', e.target.value ? Number(e.target.value) : null)}
                  placeholder="28"
                />
              </Field>
            ) : (
              <Field label={t('bodies.field.payloadCapacityKg')}>
                <Input type="number" min={0} max={100000} dir="ltr"
                  value={form.payloadCapacityKg ?? ''}
                  onChange={(e) => update('payloadCapacityKg', e.target.value ? Number(e.target.value) : null)}
                  placeholder="25000"
                />
              </Field>
            )}
            <Field label={t('bodies.field.manufacturingYear')}>
              <Input type="number" min={1980} max={2100} dir="ltr"
                value={form.manufacturingYear ?? ''}
                onChange={(e) => update('manufacturingYear', e.target.value ? Number(e.target.value) : null)}
              />
            </Field>
            <Field label={t('bodies.field.manufacturingCountry')}>
              <Input value={form.manufacturingCountry ?? ''} onChange={(e) => update('manufacturingCountry', e.target.value)} />
            </Field>
            <Field label={t('bodies.field.status')}>
              <Select value={form.status ?? 'active'} onChange={(v) => update('status', v as BodyStatus)}>
                {STATUSES.map((s) => <option key={s} value={s}>{t(`bodies.status.${s}`)}</option>)}
              </Select>
            </Field>
          </div>
        </fieldset>

        {/* Ownership — PR-RIYADH-FIX-2: Owned/Rented toggle removed. The
            investor is always the owner; rent = 0 means company-owned. */}
        <fieldset className="space-y-3">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t('bodies.section.ownership')}
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={t('bodies.field.ownerInvestor')} required>
              <Select value={form.ownerInvestorId ?? ''} onChange={(v) => update('ownerInvestorId', v)} placeholder={t('vehicles.placeholder.selectInvestor')}>
                {investors.map((inv) => (
                  <option key={inv.id} value={inv.id}>{inv.code} — {inv.fullNameEn}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('bodies.field.legalEntity')} required>
              <Select value={form.legalEntityId ?? ''} onChange={(v) => update('legalEntityId', v)} placeholder={t('vehicles.placeholder.selectLegalEntity')}>
                {legalEntities.map((le) => (
                  <option key={le.id} value={le.id}>{le.code} — {le.nameEn}</option>
                ))}
              </Select>
            </Field>
            <Field label="Monthly rent (SAR) — 0 if company-owned">
              <Input type="number" step="0.01" min={0} dir="ltr"
                value={form.monthlyRentSar ?? 0}
                onChange={(e) => update('monthlyRentSar', e.target.value === '' ? 0 : Number(e.target.value))}
                placeholder="0"
                className="font-mono"
              />
            </Field>
            <Field label={t('bodies.field.purchasePriceSar') + ' (optional)'}>
              <Input type="number" step="0.01" min={0} dir="ltr"
                value={form.purchasePriceSar ?? ''}
                onChange={(e) => update('purchasePriceSar', e.target.value ? Number(e.target.value) : null)}
                placeholder="50000"
                className="font-mono"
              />
            </Field>
            {(form.monthlyRentSar ?? 0) > 0 && (
              <>
                <Field label={t('bodies.field.rentStartDate')}>
                  <Input type="date" value={form.rentStartDate ?? ''} onChange={(e) => update('rentStartDate', e.target.value)} />
                </Field>
                <Field label={t('bodies.field.rentEndDate')}>
                  <Input type="date" value={form.rentEndDate ?? ''} onChange={(e) => update('rentEndDate', e.target.value)} />
                </Field>
              </>
            )}
            <Field label={t('bodies.field.purchaseDate') + ' (optional)'}>
              <Input type="date" value={form.purchaseDate ?? ''} onChange={(e) => update('purchaseDate', e.target.value)} />
            </Field>
          </div>
        </fieldset>

        {/* SWA License — PR-RIYADH-FIX-2 */}
        <fieldset className="space-y-3">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            SWA License
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="SWA license number">
              <Input value={form.swaLicenseNumber ?? ''} onChange={(e) => update('swaLicenseNumber', e.target.value)} placeholder="SWA-NNNN" className="font-mono" />
            </Field>
            <Field label="SWA license expiry">
              <Input type="date" value={form.swaLicenseExpiry ?? ''} onChange={(e) => update('swaLicenseExpiry', e.target.value)} />
            </Field>
          </div>
          <Field label="SWA license document">
            {initial?.id ? (
              <div className="flex items-center gap-3 flex-wrap rounded-md border border-border p-3">
                {swaFileUrl ? (
                  <AuthedImage
                    src={swaFileUrl}
                    alt="SWA license"
                    className="w-20 h-20 rounded border border-border object-cover cursor-pointer"
                    onClick={() => swaFileUrl && viewFile(swaFileUrl.split('?')[0])}
                    emptyFallback={
                      <div className="w-20 h-20 rounded border border-border bg-muted/40 inline-flex items-center justify-center text-[10px] uppercase text-muted-foreground">
                        <FileText className="h-5 w-5" />
                      </div>
                    }
                  />
                ) : (
                  <div className="w-20 h-20 rounded border border-dashed border-border bg-muted/30 inline-flex items-center justify-center text-[10px] uppercase text-muted-foreground">
                    None
                  </div>
                )}
                <div className="flex-1 text-xs text-muted-foreground">
                  JPEG/PNG/PDF · 10 MB max.
                </div>
                <label className="inline-flex items-center justify-center rounded-md border border-input bg-background px-3 h-9 text-sm font-medium hover:bg-muted cursor-pointer gap-2">
                  {swaUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  {swaFileUrl ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    className="sr-only"
                    disabled={swaUploading}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSwaFile(f); e.target.value = ''; }}
                  />
                </label>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">Save the body first to enable the SWA license upload.</p>
            )}
            {swaError && <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">{swaError}</p>}
          </Field>
        </fieldset>

        {/* Axles — PR-RIYADH-FIX-2 */}
        <fieldset className="space-y-3">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            Axles
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Axle count">
              <Input type="number" min={2} max={10} step={1} value={form.axleCount ?? ''}
                onChange={(e) => update('axleCount', e.target.value ? Number(e.target.value) : null)}
                placeholder="3"
              />
            </Field>
            <Field label="Axle configuration">
              <Select value={form.axleConfig ?? ''} onChange={(v) => {
                update('axleConfig', v);
                const preset = AXLE_CONFIGS.find((p) => p.config === v);
                if (preset && form.axleCount == null) update('axleCount', preset.count);
              }} placeholder="—">
                {AXLE_CONFIGS.map((p) => <option key={p.config} value={p.config}>{p.config}</option>)}
                <option value="multi-axle">multi-axle</option>
              </Select>
            </Field>
          </div>
        </fieldset>

        <Field label={t('bodies.field.notes')}>
          <textarea
            value={form.notes ?? ''}
            onChange={(e) => update('notes', e.target.value)}
            rows={3}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </Field>

        {error && (
          <p className="text-sm text-destructive p-3 rounded bg-destructive/10 border border-destructive/30" role="alert">
            {error}
          </p>
        )}
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-6 border-t border-border bg-muted/30 shrink-0">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>{t('common.cancel')}</Button>
        )}
        <Button type="submit" variant="gold" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitLabel ?? t('common.save')}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, required, children, className }: { label: React.ReactNode; required?: boolean; children: React.ReactNode; className?: string }) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label} {required && <span className="text-destructive">*</span>}</Label>
      {children}
    </div>
  );
}

function Select({ value, onChange, children, placeholder }: { value: string; onChange: (v: string) => void; children: React.ReactNode; placeholder?: string }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {children}
    </select>
  );
}
